// Enterprise offers, as the hub's system/enterprise and auth/offer send them.

import PhoneItem from "../../../models/PhoneItem"

export interface QuoteLine {
    key?: string
    label?: string
    unit?: string
    quantity: number
    listUnitPrice?: number
    unitPrice: number
    overridden?: boolean
    unitCost?: number
    oneTime?: boolean
    total?: number
    listTotal?: number
}

export interface ChangeRequest {
    at: string
    name?: string
    text?: string
}

export interface BillingDetails {
    legalName?: string
    addressLine1?: string
    addressLine2?: string
    city?: string
    state?: string
    postalCode?: string
    country?: string
    taxId?: string
    poNumber?: string
    billingContactName?: string
    billingContactEmail?: string
    savedAt?: string
}

export interface Signature {
    name?: string
    title?: string
    signedAt?: string
    ip?: string
    userAgent?: string
    documents: { key?: string, title?: string, fingerprint?: string }[]
}

export type QuoteStatus = "draft" | "sent" | "accepted" | "active" | "expired" | "revoked" | "superseded"

export interface Quote {
    id?: string
    number: number
    version: number
    groupId?: string
    status: QuoteStatus

    companyName?: string
    contactName?: string
    contactEmail?: string
    contactPhone?: string
    contactPhoneItem?: PhoneItem
    existingUserId?: string

    lines: QuoteLine[]
    includedUsers: number
    includedMessages: number
    includedMinutes: number
    includedNumbers: number

    overageMessageRate: number
    overageMinuteRate: number
    extraUserPrice: number
    extraNumberPrice: number

    whatsAppIncluded?: boolean
    telegramIncluded?: boolean
    extensionsSupportIncluded?: boolean
    callRecordingIncluded?: boolean
    recordingRetentionMonths: number
    supportTier?: string

    termMonths: number
    billing: "monthly" | "annual"
    autoRenew?: boolean
    renewalUpliftPercent: number
    validUntil: string
    customerNote?: string
    internalNote?: string

    listMonthly: number
    monthly: number
    listOneTime: number
    oneTime: number
    discountPercent: number
    estimatedMonthlyCost: number
    estimatedOneTimeCost: number
    marginPercent: number
    effectivePerMessage: number
    effectivePerUser: number
    periodPrice: number
    firstCharge: number
    requiredApproval: "none" | "manager" | "director"
    blockers: string[]

    approvedLevel?: string
    approvedByName?: string
    approvedAt?: string
    approvalNote?: string

    linkToken?: string
    sentAt?: string
    linkExpiresAt?: string
    revokedAt?: string
    revokedReason?: string
    openCount: number
    lastOpenedAt?: string
    changeRequests: ChangeRequest[]

    billingDetails?: BillingDetails
    signature?: Signature
    smsRegistrationStatus: string
    smsRegistrationNote?: string

    accountUserId?: string
    activatedAt?: string

    createdByName?: string
    createdAt: string
    modifiedAt: string
}

export interface QuoteEvent {
    id?: string
    version: number
    kind?: string
    note?: string
    byName?: string
    ip?: string
    at: string
}

export interface QuoteDetail {
    quote: Quote
    versions: Quote[]
    events: QuoteEvent[]
    link?: string
    existingAccountName?: string
}

export interface Tier {
    from: number
    unitPrice: number
}

export interface EnterpriseItem {
    key?: string
    label?: string
    unit?: string
    allowance?: string
    listPrice: number
    unitCost: number
    oneTime?: boolean
    tiers: Tier[]
}

export interface EnterpriseSettings {
    id?: string
    items: EnterpriseItem[]
    messageCost: number
    minuteCost: number
    selfApproveMaxDiscount: number
    managerApproveMaxDiscount: number
    directorBelowMargin: number
    defaultValidDays: number
    serviceAgreementText?: string
    dpaText?: string
    modifiedAt?: string
}

// The offer as the customer sees it on their link.
export interface OfferView {
    reference?: string
    status?: QuoteStatus
    closedReason?: string
    companyName?: string
    contactName?: string
    contactEmail?: string
    lines: { label?: string, unit?: string, quantity: number, unitPrice: number, total: number, oneTime?: boolean }[]
    monthly: number
    oneTime: number
    periodPrice: number
    billing?: string
    includedUsers: number
    includedMessages: number
    includedMinutes: number
    includedNumbers: number
    overageMessageRate: number
    overageMinuteRate: number
    extraUserPrice: number
    extraNumberPrice: number
    whatsAppIncluded?: boolean
    telegramIncluded?: boolean
    extensionsSupportIncluded?: boolean
    callRecordingIncluded?: boolean
    recordingRetentionMonths: number
    supportTier?: string
    termMonths: number
    autoRenew?: boolean
    renewalUpliftPercent: number
    validUntil: string
    customerNote?: string
    forExistingAccount?: boolean
    chargeToday: number
    firstRenewalAt?: string
    billingDetails?: BillingDetails
    signed?: boolean
    signedName?: string
    signedAt?: string
    documents: { key?: string, title?: string, text?: string }[]
    smsRegistrationStatus?: string
    accountStarted?: boolean
    activated?: boolean
    changeRequests: number
}

export const statusText: Record<QuoteStatus, string> = {
    draft: "Draft",
    sent: "Sent",
    accepted: "Signed",
    active: "Active",
    expired: "Expired",
    revoked: "Withdrawn",
    superseded: "Replaced"
}

export const statusClass: Record<QuoteStatus, string> = {
    draft: "badge-light",
    sent: "badge-light-primary",
    accepted: "badge-light-info",
    active: "badge-light-success",
    expired: "badge-light-warning",
    revoked: "badge-light-danger",
    superseded: "badge-light-dark"
}

export const smsText: Record<string, string> = {
    not_started: "Not started",
    submitted: "Submitted to carriers",
    approved: "Approved",
    rejected: "Rejected"
}
